/**
 * gift-wrap.js
 * Handles the custom gift wrap block frontend UI and interaction.
 * Stage 2: Cart Integration, Intercept Add-to-Cart.
 */

(function () {
  'use strict';

  class GiftWrapBlock {
    constructor(el) {
      this.el        = el;
      this.blockId   = el.id.replace('GiftWrapBlock-', '');
      this.radios    = el.querySelectorAll('.gift-wrap-block__radio');
      this.panel     = el.querySelector('.gift-wrap-block__panel');
      this.textarea  = el.querySelector('#GiftWrapMessage-' + this.blockId);
      this.counter   = el.querySelector('#GiftWrapCount-'   + this.blockId);

      this._init();
    }

    _init() {
      this._bindToggle();
      this._bindCharCount();
      this._syncToggleState();
    }

    _bindToggle() {
      if (!this.radios.length || !this.panel) return;
      this.radios.forEach(radio => {
        radio.addEventListener('change', () => {
          this._syncToggleState();
        });
      });
    }

    _syncToggleState() {
      if (!this.radios.length || !this.panel) return;
      let open = false;
      let yesRadio = Array.from(this.radios).find(r => r.value === 'yes');
      if (yesRadio && yesRadio.checked) {
        open = true;
      }
      this.panel.hidden = !open;
      this.panel.setAttribute('aria-hidden', String(!open));
      if (yesRadio) yesRadio.setAttribute('aria-expanded', String(open));
    }

    _bindCharCount() {
      if (!this.textarea || !this.counter) return;
      
      const updateCount = () => {
        this.counter.textContent = this.textarea.value.length;
      };

      this.textarea.addEventListener('input', updateCount);
      // Initialize count in case there's a default value
      updateCount();
    }
  }

  function initAll() {
    document.querySelectorAll('.gift-wrap-block[id^="GiftWrapBlock-"]').forEach(function (el) {
      if (!el._giftWrapInstance) {
        el._giftWrapInstance = new GiftWrapBlock(el);
      }
    });
  }

  function submitGiftWrapCart(productForm, giftWrapBlock, isBuyNow) {
    if (productForm._isProcessingGiftWrap) return;
    productForm._isProcessingGiftWrap = true;

    const form = productForm.tagName === 'FORM' ? productForm : productForm.querySelector('form');
    if (!form) {
      productForm._isProcessingGiftWrap = false;
      return;
    }

    const submitButton = isBuyNow 
      ? (productForm.querySelector('.shopify-payment-button__button') || productForm.submitButton)
      : productForm.submitButton;

    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') {
      productForm._isProcessingGiftWrap = false;
      return;
    }

    if (typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage();
    }

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
    }
    productForm.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

    // Generate unique Group ID to link parent product with gift wrap
    const groupId = 'gift_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();

    // Retrieve form data to preserve all parent customization inputs and selling plans
    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const mainQuantity = parseInt(formData.get('quantity') || 1, 10);
    const sellingPlan = formData.get('selling_plan');

    // Extract existing properties from the form (e.g. engravings or personalization)
    const parentProperties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.slice(11, -1);
        parentProperties[propName] = value;
      }
    }
    parentProperties['_gift_group_id'] = groupId;

    const parentTitle = giftWrapBlock.dataset.parentTitle || 'Parent Product';

    // Retrieve gift wrap variant
    const giftVariantSelect = giftWrapBlock.querySelector('.gift-wrap-block__select');
    const giftHiddenInput = giftWrapBlock.querySelector('input[type="hidden"][name="gift_wrap_variant"]');
    const giftVariantId = giftVariantSelect ? giftVariantSelect.value : (giftHiddenInput ? giftHiddenInput.value : null);

    if (!giftVariantId) {
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage("Selected gift wrap option is unavailable.");
      }
      if (submitButton) {
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
      }
      productForm.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      productForm._isProcessingGiftWrap = false;
      return;
    }

    // Retrieve personalization fields
    const toValue = giftWrapBlock.querySelector('[name="gift_wrap_to"]')?.value || '';
    const fromValue = giftWrapBlock.querySelector('[name="gift_wrap_from"]')?.value || '';
    const messageValue = giftWrapBlock.querySelector('[name="gift_wrap_message"]')?.value || '';
    const styleValue = giftWrapBlock.querySelector('[name="gift_wrap_style"]')?.value || '';
    const typeValue = giftWrapBlock.querySelector('[name="gift_wrap_type"]')?.value || '';

    // Prepare items list for bundling
    const parentItem = {
      id: mainVariantId,
      quantity: mainQuantity,
      properties: parentProperties
    };

    if (sellingPlan) {
      parentItem.selling_plan = sellingPlan;
    }

    const giftProperties = {
      _gift_group_id: groupId,
      _gift_wrap: 'true',
      To: toValue,
      From: fromValue
    };

    if (styleValue) {
      giftProperties['Gift Wrap Option'] = styleValue;
    }

    if (typeValue) {
      giftProperties['Wrapping is'] = typeValue;
    }

    giftProperties['Personal Message'] = messageValue;
    giftProperties['Parent Product Reference'] = parentTitle;

    const items = [
      parentItem,
      {
        id: giftVariantId,
        quantity: mainQuantity,
        properties: giftProperties
      }
    ];

    const config = typeof fetchConfig === 'function' ? fetchConfig('json') : { method: 'POST', headers: {} };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = 'application/json';

    const payload = { items };

    if (!isBuyNow && productForm.cart) {
      payload.sections = productForm.cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      productForm.cart.setActiveElement(document.activeElement);
    }

    config.body = JSON.stringify(payload);

    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const checkoutUrl = (window.routes && window.routes.cart_url) 
      ? window.routes.cart_url.replace(/\/cart$/, '') + '/checkout' 
      : '/checkout';

    fetch(cartAddUrl, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Cart API returned an error
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                source: productForm.dataset.source,
                productVariantId: mainVariantId,
                errors: response.description,
                message: response.message,
              },
            })
          );
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: mainVariantId,
              errors: response.description,
              message: response.message,
            });
          }
          if (typeof productForm.handleErrorMessage === 'function') {
            productForm.handleErrorMessage(response.description);
          } else {
            alert(response.description || "Error adding item to cart.");
          }
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
          productForm.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          productForm._isProcessingGiftWrap = false;
          return;
        }

        if (isBuyNow) {
          window.location.href = checkoutUrl;
          return;
        }

        if (!productForm.cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        // Dispatch variant add events
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: { id: mainVariantId },
              quantity: mainQuantity,
              formElement: form,
              sectionId: productForm.dataset.source,
            },
          })
        );

        // Get updated cart state
        fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`)
          .then((r) => r.json())
          .then((cartState) => {
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: {
                  cart: cartState,
                  sectionId: productForm.dataset.source,
                },
              })
            );
          });

        if (!productForm.error) {
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: mainVariantId,
            });
          }
        }
        productForm.error = false;

        const quickAddModal = productForm.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => {
            setTimeout(() => { productForm.cart.renderContents(response) });
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          productForm.cart.renderContents(response);
        }
      })
      .catch((e) => {
        console.error(e);
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        }
        productForm.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      })
      .finally(() => {
        if (!isBuyNow) {
          if (submitButton) {
            submitButton.classList.remove('loading');
            if (!productForm.error) submitButton.removeAttribute('aria-disabled');
          }
          if (productForm.cart && productForm.cart.classList.contains('is-empty')) {
            productForm.cart.classList.remove('is-empty');
          }
          productForm.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          productForm._isProcessingGiftWrap = false;
        }
      });
  }

  // Intercept clicks on dynamic checkout buttons ("Buy it Now")
  document.addEventListener('click', function (evt) {
    const buyBtn = evt.target.closest('.shopify-payment-button__button') || evt.target.closest('.shopify-payment-button');
    if (!buyBtn) return;

    const pForm = buyBtn.closest('product-form') || buyBtn.closest('form');
    if (!pForm) return;
    const productForm = pForm.tagName === 'PRODUCT-FORM' ? pForm : (pForm.closest('product-form') || pForm);

    const section = productForm.closest('.shopify-section') || document;
    const giftWrapBlock = section.querySelector('.gift-wrap-block');
    if (!giftWrapBlock) return;

    const yesRadio = giftWrapBlock.querySelector('.gift-wrap-block__radio--yes');
    const noRadio = giftWrapBlock.querySelector('.gift-wrap-block__radio--no');
    
    const isMandatory = giftWrapBlock.dataset.isMandatory === 'true';

    // JS-based validation for mandatory selection
    if (isMandatory && yesRadio && noRadio && !yesRadio.checked && !noRadio.checked) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage("Please select whether you want to add gift wrap.");
      } else {
        alert("Please select whether you want to add gift wrap.");
      }
      return;
    }

    if (yesRadio && yesRadio.checked) {
      const styleSelect = giftWrapBlock.querySelector('[name="gift_wrap_style"]');
      const typeSelect = giftWrapBlock.querySelector('[name="gift_wrap_type"]');
      
      if ((styleSelect && !styleSelect.value) || (typeSelect && !typeSelect.value)) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();
        
        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage("Please select a Gift Wrap Option and Wrapping type.");
        } else {
          alert("Please select a Gift Wrap Option and Wrapping type.");
        }
        return;
      }

      // Gift Wrap is active! Intercept Buy Now click, add both items to cart, and redirect to checkout
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();

      submitGiftWrapCart(productForm, giftWrapBlock, true);
    }
  }, true);

  // Intercept the native ProductForm submissions using a capturing event listener.
  document.addEventListener('submit', function (evt) {
    const form = evt.target;
    const productForm = form.closest('product-form') || form;
    if (!productForm) return;

    // Find the gift wrap block in the same section/template (they are siblings, not nested)
    const section = productForm.closest('.shopify-section') || document;
    const giftWrapBlock = section.querySelector('.gift-wrap-block');
    if (!giftWrapBlock) return;

    const yesRadio = giftWrapBlock.querySelector('.gift-wrap-block__radio--yes');
    const noRadio = giftWrapBlock.querySelector('.gift-wrap-block__radio--no');
    
    const isMandatory = giftWrapBlock.dataset.isMandatory === 'true';

    // JS-based validation for mandatory selection
    if (isMandatory && yesRadio && noRadio && !yesRadio.checked && !noRadio.checked) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage("Please select whether you want to add gift wrap.");
      } else {
        alert("Please select whether you want to add gift wrap.");
      }
      return;
    }

    if (!yesRadio || !yesRadio.checked) {
      // If "No" is selected, clear any previous errors
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage(''); // clear error
      }
      return; // Let native submission happen
    }

    const styleSelect = giftWrapBlock.querySelector('[name="gift_wrap_style"]');
    const typeSelect = giftWrapBlock.querySelector('[name="gift_wrap_type"]');
    
    if ((styleSelect && !styleSelect.value) || (typeSelect && !typeSelect.value)) {
      evt.preventDefault();
      evt.stopPropagation();
      evt.stopImmediatePropagation();
      
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage("Please select a Gift Wrap Option and Wrapping type.");
      } else {
        alert("Please select a Gift Wrap Option and Wrapping type.");
      }
      return;
    }

    const isBuyNow = !!(evt.submitter && (evt.submitter.classList.contains('shopify-payment-button__button') || evt.submitter.closest('.shopify-payment-button')));

    // Gift wrap is active! Intercept standard form submission.
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();

    submitGiftWrapCart(productForm, giftWrapBlock, isBuyNow);
  }, true); // capturing phase!

  // Initialize UI features
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:load',   initAll);
})();